/**
 * BetaBadge — small pill that flags a studio as part of the Orbit
 * beta cohort. Renders nothing for non-beta orgs so callers can drop
 * it into any header without a conditional wrapper.
 *
 * Default variant: NovaHamsterWheel + "Beta tester" label.
 * compact:        tiny "BETA" pill, useful in dense rows.
 */
import { useSubscription } from '@/hooks/useSubscription';
import { NovaHamsterWheel } from '@/components/nova/NovaHamsterWheel';
import { cn } from '@/lib/utils';

interface BetaBadgeProps {
  compact?: boolean;
  className?: string;
}

export function BetaBadge({ compact = false, className }: BetaBadgeProps) {
  const { hasBetaAccess } = useSubscription();
  if (!hasBetaAccess) return null;

  if (compact) {
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full border border-orbit-orange/40 bg-orbit-orange/10 text-orbit-orange',
          'px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]',
          className,
        )}
      >
        Beta
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-full border border-orbit-orange/40 bg-orbit-orange/10 text-orbit-orange',
        'pl-1 pr-3 py-1',
        className,
      )}
    >
      <NovaHamsterWheel size={28} />
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">
        Beta tester
      </span>
    </span>
  );
}

export default BetaBadge;